import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, MessageCircle, Phone, Loader2, TrendingUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getUpsell, updateUpsellCase } from "@/lib/api";
import { numberLocale } from "@/lib/money";

// Upsell list (backend routes/upsell.py): members whose training says they're ready for more: 6WC
// finishers who trained consistently (finisher prices), heavy trainers on short plans (Annual), and
// near-daily trainers (Semi-Private). The offer shows this gym's catalog prices (before tax). A person
// sends the message; a case closes by itself when the member buys something new.

const err = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;
const fmt = (n) => new Intl.NumberFormat(numberLocale()).format(Math.round(n || 0));
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "");
const TINT = {
  finisher: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40",
  annual: "bg-sky-500/15 text-sky-200 border-sky-500/40",
  spt: "bg-violet-500/15 text-violet-200 border-violet-500/40",
};
const waLink = (phone, text) => {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? `91${digits}` : digits}?text=${encodeURIComponent(text)}`;
};

function Offer({ m }) {
  const cur = window.__ACTIVE_CURRENCY_SYMBOL__ || "";
  return (
    <div className="mt-2 flex flex-wrap gap-2 text-xs">
      {m.offer.plans.map((p) => (
        <span key={p.plan} className="rounded-md border border-[#262626] bg-[#0d0d0d] px-2 py-1 text-zinc-200">
          {p.plan} <span className="font-mono text-emerald-300">{p.price ? `${cur}${fmt(p.price)}` : "price not set"}</span>
        </span>
      ))}
      {m.offer.current?.price && (
        <span className="text-zinc-500 self-center">now on {m.offer.current.plan} ({cur}{fmt(m.offer.current.price)})</span>
      )}
      <span className="text-zinc-600 self-center">before tax</span>
    </div>
  );
}

function Case({ m, outcomes, onSaved }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (outcome) => {
    setBusy(true);
    try {
      const r = await updateUpsellCase(m.case_id, { outcome, note });
      toast.success(r.closed ? `${m.name}: ${outcomes[outcome].toLowerCase()}` : "Saved");
      setNote(""); setOpen(false); onSaved();
    } catch (e) { toast.error(err(e, "Couldn't save")); }
    finally { setBusy(false); }
  };
  const wa = waLink(m.phone, m.message);
  return (
    <li className="card-surface p-4" data-testid={`upsell-${m.case_id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-zinc-100">{m.name}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full border ${TINT[m.reason]}`}>{m.reason_label}</span>
            {m.outcome === "offered" && <span className="text-[11px] px-2 py-0.5 rounded-full border border-sky-500/40 text-sky-200">Offered</span>}
          </div>
          <div className="text-xs text-zinc-400 mt-1">
            {m.programme || "Membership"}{m.end_date ? `, ends ${day(m.end_date)}` : ""} · {m.per_week} sessions a week lately
            {m.last_visit ? ` · last in ${day(m.last_visit)}` : ""}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            {m.member_manager ? `Member manager: ${m.member_manager}` : "No member manager"}{m.coach ? ` · Coach: ${m.coach}` : ""}
          </div>
          <Offer m={m} />
          {m.notes.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-zinc-400">
              {m.notes.slice(-3).map((n, i) => <li key={i}>{day(n.at)} · {n.by}: {n.outcome ? `${outcomes[n.outcome]}. ` : ""}{n.text}</li>)}
            </ul>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {wa && <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-sm text-white"><MessageCircle className="h-4 w-4" />WhatsApp</a>}
          {m.phone && <a href={`tel:${m.phone}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-[#2a2a2a] text-sm text-zinc-200"><Phone className="h-4 w-4" />Call</a>}
          <button type="button" onClick={() => setOpen((v) => !v)} className="h-9 px-3 rounded-full border border-[#2a2a2a] text-sm text-zinc-200">Log what happened</button>
        </div>
      </div>
      <div className="mt-3 rounded-lg border border-[#1f1f1f] bg-[#0a0a0a] p-3 text-sm text-zinc-300 flex items-start gap-2">
        <span className="flex-1">{m.message}</span>
        <button type="button" title="Copy message" className="text-zinc-500 hover:text-zinc-200"
          onClick={() => { navigator.clipboard?.writeText(m.message); toast.success("Message copied"); }}><Copy className="h-4 w-4" /></button>
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="What did they say? (needed for not now / not interested)"
            className="w-full bg-[#0a0a0a] border border-[#262626] rounded-md px-3 py-2 text-sm text-zinc-100" />
          <div className="flex flex-wrap gap-2">
            {Object.entries(outcomes).map(([k, label]) => (
              <button key={k} type="button" disabled={busy} onClick={() => save(k)}
                className="h-8 px-3 rounded-full text-xs border border-[#2a2a2a] text-zinc-200">{label}</button>
            ))}
            {note.trim() && <button type="button" disabled={busy} onClick={() => save(null)} className="h-8 px-3 rounded-full text-xs bg-zinc-100 text-zinc-900">Save note only</button>}
          </div>
        </div>
      )}
    </li>
  );
}

export default function Upsell() {
  const { activeFacility } = useAuth();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = useCallback(() => {
    getUpsell().then(setData).catch((e) => toast.error(err(e, "Couldn't load the upsell list")));
  }, []);
  useEffect(() => { setData(null); load(); }, [load, activeFacility?.id]);
  const members = useMemo(() => (data?.members || []).filter((m) => filter === "all" || m.reason === filter), [data, filter]);
  const counts = useMemo(() => {
    const c = {};
    (data?.members || []).forEach((m) => { c[m.reason] = (c[m.reason] || 0) + 1; });
    return c;
  }, [data]);
  const value = (data?.members || []).reduce((s, m) => s + (m.value || 0), 0);

  return (
    <div className="space-y-6" data-testid="upsell-page">
      <div>
        <div className="eyebrow">Retention · {activeFacility?.name || "this gym"}</div>
        <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight inline-flex items-center gap-2"><TrendingUp className="h-7 w-7 text-emerald-400" />Upsell</h2>
        <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
          Members whose training says they&apos;re ready for more, with what to offer at this gym&apos;s prices. Send the message from your
          phone, then log what happened. It closes by itself when they buy.
        </p>
      </div>
      {!data && <div className="text-zinc-500 text-sm inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading</div>}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="card-surface p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Ready now</div>
              <div className="font-display text-2xl font-extrabold mt-1 text-emerald-300">{data.members.length}</div>
            </div>
            <div className="card-surface p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Top offer value</div>
              <div className="font-display text-2xl font-extrabold mt-1 text-amber-300">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmt(value)}</div>
            </div>
            <div className="card-surface p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Bought this month</div>
              <div className="font-display text-2xl font-extrabold mt-1 text-emerald-300">{data.this_month.bought || 0}</div>
            </div>
            <div className="card-surface p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Not now / not interested</div>
              <div className="font-display text-2xl font-extrabold mt-1 text-zinc-300">{data.this_month.not_now || 0} / {data.this_month.no || 0}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {[["all", `All ${data.members.length}`], ...Object.entries(data.reasons).map(([k, v]) => [k, `${v} ${counts[k] || 0}`])].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setFilter(k)}
                className={`h-8 px-3 rounded-full text-xs ${filter === k ? "bg-zinc-100 text-zinc-900" : "border border-[#2a2a2a] text-zinc-400"}`}>{label}</button>
            ))}
          </div>
          {members.length === 0
            ? <div className="card-surface p-6 text-sm text-zinc-400">Nobody here right now.</div>
            : <ul className="space-y-3">{members.map((m) => <Case key={m.case_id} m={m} outcomes={data.outcomes} onSaved={load} />)}</ul>}
        </>
      )}
    </div>
  );
}
